import { createContext, useContext, type ReactNode } from 'react';
import { numericValueOrNull } from '../../utils/decimalValueDto';

export type GridCellLoadingAlign = 'left' | 'center' | 'right';

export interface GridCellLoadingBarProps {
  align?: GridCellLoadingAlign;
  testId?: string;
}

/**
 * Usual React skeleton for a grid cell that is still waiting: a semi-transparent
 * horizontal bar that pulses in place of the value.
 */
export function GridCellLoadingBar({ align = 'right', testId }: GridCellLoadingBarProps) {
  const justify = align === 'left' ? 'justify-start' : align === 'center' ? 'justify-center' : 'justify-end';

  return (
    <span
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading"
      data-testid={testId}
      className={`flex w-full items-center ${justify}`}
    >
      <span className="inline-block h-2.5 w-3/4 max-w-[5.5rem] rounded-sm bg-slate-400/40 animate-pulse" />
    </span>
  );
}

const GridCellLoadingContext = createContext(false);

export function useGridCellLoading(): boolean {
  return useContext(GridCellLoadingContext);
}

export function GridCellLoadingProvider({
  loading = false,
  children,
}: {
  loading?: boolean;
  children: ReactNode;
}) {
  return <GridCellLoadingContext.Provider value={loading}>{children}</GridCellLoadingContext.Provider>;
}

/** True while the grid is awaiting a value that has not arrived yet (empty dash, not a missing-price `_`). */
export function isGridCellValuePending(loading: boolean, dto: unknown): boolean {
  return loading && numericValueOrNull(dto) == null;
}
